/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ID, WithVersion } from '@gh/common-shared-data';
import { createEnum, EnumSet } from '@gh/core-data';

import { CommunicationAttachmentDto } from '../../communications';
import { ContactDeliveryMethod } from '../../contacts/model/contact-details-shared.dto';

export const JobOrderStatus = createEnum('JOB_ORDER_STATUS', {
  Pending: 1,
  Accepted: 2,
  Rejected: 3,
});

export type JobOrderStatus = EnumSet<typeof JobOrderStatus>;

export const JobOrderMethod = createEnum('SEND_ORDER_BY', {
  Email: 1,
  Printer: 2,
});
export type JobOrderMethod = EnumSet<typeof JobOrderMethod>;

export const JobOrderDelivery = createEnum('ORDER_DELIVERY_DATE_METHOD', {
  Schedule: 1,
  Entered: 2,
});
export type JobOrderDelivery = EnumSet<typeof JobOrderDelivery>;

export type JobOrderListMasterInfoDto = {
  id: ID;
  jobNumber: string;
  contactName: string;
  contactId: ID;
  siteAddress: string;
  produceInProgress: boolean;
  isFinalized: boolean;
  isNotProceeding: boolean;
  jobDocsFolderId: ID;
  extraOrderDocs: CommunicationAttachmentDto[];
  csSignedByClientDate: Date;
  csSignedByOfficeDate: Date;
  estimateAuthorizedDate?: Date;
  scheduleStartDate: Date;
  insurancePolicyNumber?: string;
  isContractSigned: boolean;
  isCouncilApproved: boolean;
  isFinanceApproved: boolean;
};
export type JobOrderListEstimatingCriteriaDto = {
  criteriaEstimate: boolean;
  criteriaCouncil: boolean;
  criteriaFinance: boolean;
  criteriaColorSelection: boolean;
  criteriaJobSchedule: boolean;
  criteriaContract: boolean;
  criteriaJobProceeding: boolean;
  criteriaInsurancePolicy: boolean;
  printRecordForm: boolean;
  ordersPrintCostCenter?: number;
};
export type JobOrderListDto = {
  job: JobOrderListMasterInfoDto;
  estimatingCriteria: JobOrderListEstimatingCriteriaDto;
  jobOrders: JobOrderListItemDto[];
};

export type JobOrderListItemDto = {
  jobOrderId: ID;
  costCenterCode: number;
  costCenterName: string;
  orderNumber: number;
  supplierName: string;
  contractReceived: Date;
  contractEnd: Date;
  deliveryDate: Date;
  amount: number;
  attachments: any[];
  method: JobOrderMethod;
  status: JobOrderStatus;
  producedMethod?: JobOrderMethod;
  producedDate?: Date;
  is6A: boolean;
  isCriteriaFailed: boolean;
};

export type JobOrderListProduceDto = {
  jobOrderIds: ID[];
  jobOrderIds6a: ID[];
  printerId?: ID;
  attachments: CommunicationAttachmentDto[];
  forcePrint: boolean;
  attachToEmailed: boolean;
  attachToPrinted: boolean;
  authCode?: string;
};

export type JobCommunicationAttachmentDto = CommunicationAttachmentDto & { isJobOrder: boolean };

export type JobOrderDetailsDto = WithVersion<{
  orderId: ID;
  orderNumber: number;
  costCenterId: ID;
  deliveryDate?: Date;
  deliveryScheduleDate?: Date;
  producedDate?: Date;
  supplierId?: ID;
  supplierName?: string;
  supplierOrderBy?: JobOrderMethod;
  attachments: JobCommunicationAttachmentDto[];
  method?: JobOrderMethod;
  producedMethod?: JobOrderMethod;
  delivery: JobOrderDelivery;
  amount: number;
  allowReprint: boolean;
}>;

export const jobOrderMethodFromDeliveryMethod = (method: ContactDeliveryMethod) => ({
  [ContactDeliveryMethod.Mail]: JobOrderMethod.Printer,
  [ContactDeliveryMethod.Email]: JobOrderMethod.Email,
})[method];

export const jobOrderMethodToDeliveryMethod = (method: JobOrderMethod) => ({
  [JobOrderMethod.Printer]: ContactDeliveryMethod.Mail,
  [JobOrderMethod.Email]: ContactDeliveryMethod.Email,
})[method];
